import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { tr } from "@/i18n/translations";
import LanguageSwitch from "@/components/LanguageSwitch";

const Home = () => {
  const { lang } = useLanguage();
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="home-page"
    >
      {videoOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.92)" }}
          onClick={() => setVideoOpen(false)}
        >
          <div
            className="relative w-full max-w-3xl mx-4"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setVideoOpen(false)}
              className="mb-3 flex items-center gap-2 na-btn na-btn-ghost text-xs"
              style={{ letterSpacing: "0.2em" }}
            >
              ← NAZAD
            </button>
            <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: "6px", overflow: "hidden", border: "1px solid rgba(34,255,119,0.3)", boxShadow: "0 0 40px rgba(34,255,119,0.15)" }}>
              <iframe
                src="https://www.youtube.com/embed/ctqZgFmeWSs?autoplay=1"
                title="NAŠA MYNER Adria Video prezentacija"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
              />
            </div>
            <p className="mt-3 text-center text-xs text-zinc-500 uppercase tracking-[0.2em]">Klikni van videa ili pritisni NAZAD da zatvori</p>
          </div>
        </div>
      )}
      <LanguageSwitch />
      <div className="na-grid" />
      <div className="na-noise" />

      <main className="relative z-10 mx-auto flex max-w-6xl flex-col items-center px-5 pb-16 pt-14 sm:px-8 sm:pt-20 lg:pt-24">
        <div className="na-anim na-delay-1 flex flex-col items-center text-center">
          <span className="na-tag" data-testid="home-tag">
            {tr("home", "tag", lang)}
          </span>
          <span
            className="mt-2 text-[11px] uppercase tracking-[0.35em] text-zinc-500 sm:text-xs"
            data-testid="home-tag-sub"
          >
            {tr("home", "tagSub", lang)}
          </span>
        </div>

        <h1
          className="na-display na-anim na-delay-2 mt-7 text-center text-4xl leading-none sm:text-6xl lg:text-7xl"
          style={{ letterSpacing: "0.14em" }}
          data-testid="home-title"
        >
          <span className="na-glow-orange">DOBRO</span>
          <span className="na-glow-green">DOŠLI</span>
        </h1>

        <p
          className="na-anim na-delay-3 mt-6 max-w-2xl text-center text-sm leading-relaxed text-zinc-400 sm:text-base"
          data-testid="home-subtitle"
        >
          {tr("home", "subtitle", lang)}
        </p>

        <button
          onClick={() => setVideoOpen(true)}
          className="na-video-banner mt-10 w-full"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="na-play-icon">▶</div>
            <div className="text-left">
              <div className="na-display text-sm text-[var(--na-green)]" style={{ letterSpacing: "0.22em" }}>POGLEDAJTE UVOD PRE NEGO ŠTO UĐETE</div>
              <div className="text-xs text-zinc-400 mt-1">5 min · NAŠA MYNER Adria · Video prezentacija</div>
            </div>
          </div>
        </button>

        <div
          className="na-anim na-delay-4 mt-14 grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:gap-8"
          data-testid="home-cards"
        >
          <Link to="/tonini-bitlab" className="na-card group block" data-testid="card-tonini">
            <div className="relative flex flex-col items-start gap-5">
              <img src="/logo-tonini-bitlab.png" alt="Tonini BitLab" className="h-16 w-auto object-contain" />
              <div>
                <h2 className="na-display text-xl text-white sm:text-2xl" style={{ letterSpacing: "0.16em" }}>
                  Tonini <span className="na-glow-green">BitLab</span>
                </h2>
                <p className="mt-3 text-sm font-medium text-orange-300/90">{tr("home", "toniniKicker", lang)}</p>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{tr("home", "toniniDesc", lang)}</p>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--na-green)] transition-transform duration-300 group-hover:translate-x-1">
                {tr("common", "learnMore", lang)} <ArrowRight size={14} />
              </div>
            </div>
          </Link>

          <Link to="/nasa-myner" className="na-card group block" data-testid="card-myner">
            <div className="relative flex flex-col items-start gap-5">
              <img src="/logo-nasa-myner.png" alt="NAŠA Myner Adria" className="h-16 w-auto object-contain" />
              <div>
                <h2 className="na-display text-xl text-white sm:text-2xl" style={{ letterSpacing: "0.16em" }}>
                  NAŠA <span className="na-glow-orange">MYNER</span> Adria
                </h2>
                <p className="mt-3 text-sm font-medium text-green-300/90">{tr("home", "mynerKicker", lang)}</p>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{tr("home", "mynerDesc", lang)}</p>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--na-orange)] transition-transform duration-300 group-hover:translate-x-1">
                {tr("common", "learnMore", lang)} <ArrowRight size={14} />
              </div>
            </div>
          </Link>
        </div>

        <a
          href="https://forms.gle/Fu4UEiWu89egjvWHA"
          className="na-contact-banner na-anim na-delay-5 mt-12"
          data-testid="contact-banner"
        >
          <div className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-4">
            <Mail size={18} className="text-[var(--na-orange)]" />
            <span className="na-display text-sm text-[var(--na-orange)] sm:text-base" style={{ letterSpacing: "0.22em" }}>
              {tr("home", "contactTitle", lang)}
            </span>
            <span className="hidden text-zinc-500 sm:inline">—</span>
            <span className="text-xs leading-relaxed text-zinc-300 sm:text-sm">{tr("home", "contactSub", lang)}</span>
          </div>
        </a>

        <a
          href="https://www.nasha-hub.com"
          className="na-hub-banner na-anim na-delay-5 mt-4 block w-full px-6 py-5 text-center rounded-sm"
          data-testid="hub-banner"
          style={{ boxShadow: "0 0 24px rgba(34,255,119,0.15)", border: "1px solid rgba(34,255,119,0.3)" }}
        >
          <div className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-4">
            <span className="na-display text-xs text-[var(--na-green)] sm:text-sm" style={{ letterSpacing: "0.22em" }}>
              {tr("home", "hubTitle", lang)}
            </span>
            <span className="hidden text-zinc-600 sm:inline">—</span>
            <span className="text-xs leading-relaxed text-zinc-400">{tr("home", "hubSub", lang)}</span>
            <span className="mt-1 text-xs font-semibold text-[var(--na-orange)] sm:mt-0">{tr("home", "hubBtn", lang)}</span>
          </div>
        </a>

        <footer className="mt-16 w-full border-t border-white/5 pt-10 text-center" data-testid="home-footer">
          <div className="mb-6 text-[10px] uppercase tracking-[0.35em] text-zinc-500">{tr("home", "foundations", lang)}</div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
            <img src="/logo-nasa-com.png" alt="NAŠA.com" className="h-10 w-auto object-contain opacity-80 transition-opacity duration-300 hover:opacity-100" />
            <img src="/logo-tonini-finance.png" alt="Tonini Finance" className="h-16 w-auto object-contain opacity-80 transition-opacity duration-300 hover:opacity-100" />
            <img src="/logo-nasa-community.png" alt="NAŠA Community" className="h-10 w-auto object-contain opacity-80 transition-opacity duration-300 hover:opacity-100" />
          </div>
          <div className="mt-10 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
            © {new Date().getFullYear()} NAŠA MYNER Adria · {tr("common", "rightsReserved", lang)}
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Home;
